import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Button, CircularProgress, Alert, Checkbox, Chip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { resolverTargetOrigin, sanitizarConexaoParaOpener } from "../utils/openerSeguro";
import { META_GRAPH_VERSION_PADRAO } from "../config/metaGraph";

/**
 * ConnectPage — Embedded Signup HOSPEDADO NO CENTRAL.
 *
 * Aberta em popup pelo HUB Local em `<central>/connect?cs=<token>`. Roda o
 * FB.login NO DOMINIO DO CENTRAL (unico no allowlist da Meta), completa o signup
 * com o secret do Central (via token opaco que identifica o tenant do Local) e
 * devolve o resultado ao opener por postMessage. Nenhum dado do Meta sai do
 * Central; nenhum dominio de Local precisa entrar no App da Meta.
 */
export default function ConnectPage() {
  const [phase, setPhase] = useState("loading"); // loading | ready | working | escolher | done | error
  // Varias paginas/contas liberadas no login: { canal, contas, vagas } vindo do servidor.
  const [escolha, setEscolha] = useState(null);
  const [marcadas, setMarcadas] = useState([]);
  const [error, setError] = useState("");
  const [cfg, setCfg] = useState(null);
  const [result, setResult] = useState(null);
  const sessionInfo = useRef({}); // waba_id / phone_number_id do evento WA_EMBEDDED_SIGNUP
  const cfgRef = useRef(null);    // ultima config da sessao (p/ ler returnOrigin na hora do post)

  const cs = new URLSearchParams(window.location.search).get("cs");

  const postToOpener = useCallback((payload) => {
    // Fase 13 (P2): targetOrigin NUNCA "*". Preferimos a origem do painel que o Central gravou
    // na sessao (`returnOrigin`), robusta ao round-trip da Meta; fallback no referrer https.
    // Sem origem confiavel, NAO se posta.
    const alvo =
      resolverTargetOrigin(cfgRef.current && cfgRef.current.returnOrigin) ||
      resolverTargetOrigin(document.referrer);
    if (!alvo || !window.opener) return;
    const seguro = { ...payload };
    if (seguro.connection) seguro.connection = sanitizarConexaoParaOpener(seguro.connection);
    try {
      window.opener.postMessage({ source: "hub_connect", ...seguro }, alvo);
    } catch (_) { /* noop */ }
  }, []);

  // Captura waba_id / phone_number_id do evento de coexistencia da Meta.
  useEffect(() => {
    const onMsg = (event) => {
      if (typeof event.data !== "string") return;
      if (!/facebook\.com$/.test((() => { try { return new URL(event.origin).hostname; } catch { return ""; } })())) return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === "WA_EMBEDDED_SIGNUP" && data.data) {
          sessionInfo.current = {
            wabaId: data.data.waba_id || sessionInfo.current.wabaId,
            phoneNumberId: data.data.phone_number_id || sessionInfo.current.phoneNumberId,
          };
        }
      } catch (_) { /* payload nao-JSON */ }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // Carrega a config publica da sessao (appId/configId/apiVersion) — sem secrets.
  useEffect(() => {
    if (!cs) { setPhase("error"); setError("Link de conexao invalido (sem token)."); return; }
    fetch(`/hub/connect-session/${encodeURIComponent(cs)}`)
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok) { setPhase("error"); setError(j.error || "Sessao invalida ou expirada."); postToOpener({ ok: false, error: j.error }); return; }
        cfgRef.current = j; setCfg(j); setPhase("ready");
      })
      .catch(() => { setPhase("error"); setError("Falha ao carregar a sessao de conexao."); });
  }, [cs, postToOpener]);

  const loadFacebookSDK = useCallback((appId, apiVersion) => {
    return new Promise((resolve, reject) => {
      if (window.FB) { resolve(); return; }
      window.fbAsyncInit = function () {
        window.FB.init({ appId, cookie: true, xfbml: false, version: apiVersion || META_GRAPH_VERSION_PADRAO });
        resolve();
      };
      if (!document.getElementById("facebook-jssdk")) {
        const s = document.createElement("script");
        s.id = "facebook-jssdk";
        s.src = "https://connect.facebook.net/pt_BR/sdk.js";
        s.async = true; s.defer = true;
        s.onerror = () => reject(new Error("Falha ao carregar o SDK do Facebook."));
        document.body.appendChild(s);
      }
    });
  }, []);

  const complete = useCallback(async (creds) => {
    setPhase("working");
    try {
      const body = { ...creds, ...sessionInfo.current };
      const r = await fetch(`/hub/connect-session/${encodeURIComponent(cs)}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      /*
       * 2+ contas liberadas: o servidor NAO criou nada e devolve a lista para o cliente marcar.
       * Pre-marca as novas ate o limite de vagas (o cliente ja as escolheu na Meta); as que a
       * empresa ja tem ficam desmarcadas — marcar reconecta.
       */
      if (j.selecionar) {
        const vagas = j.vagas;
        const novas = (j.contas || []).filter((c) => !c.jaConectada).map((c) => c.id);
        setEscolha({ canal: j.canal, contas: j.contas || [], vagas });
        setMarcadas((prev) => (creds.contas ? prev : (vagas == null ? novas : novas.slice(0, vagas))));
        setPhase("escolher");
        if (!r.ok) setError(j.error || "Revise a selecao.");
        return;
      }
      if (!r.ok) throw new Error(j.error || "Falha ao completar a conexao.");
      setResult(j); setPhase("done");
      postToOpener({ ok: true, connection: j });
      // Com falha parcial a janela fica aberta para o cliente ler o que nao conectou.
      if (!(j.falhas && j.falhas.length)) setTimeout(() => { try { window.close(); } catch (_) {} }, 1800);
    } catch (e) {
      setError(e.message);
      // Na escolha, o erro fica na tela e o cliente corrige sem refazer o login.
      if (creds.contas) { setPhase("escolher"); return; }
      setPhase("ready");
      postToOpener({ ok: false, error: e.message });
    }
  }, [cs, postToOpener]);

  const start = useCallback(async () => {
    setError("");
    if (!cfg?.appId) { setError("App Meta nao configurado no Central."); return; }
    try {
      await loadFacebookSDK(cfg.appId, cfg.apiVersion);
      /*
       * SCOPE POR CANAL. O Instagram usava o scope do Facebook e por isso nao
       * funcionava: sem `instagram_basic`, o `/me/accounts` NAO devolve o campo
       * `instagram_business_account`, e o HUB concluia "nenhuma pagina tem conta
       * Instagram vinculada" — mentira: a pagina tem, o token e que nao podia ver.
       * E sem `instagram_manage_messages` a conexao nasceria surda (sem DM).
       *
       * Este e o caminho do Instagram VIA PAGINA DO FACEBOOK, que usa permissoes ja
       * aprovadas no App. Nao confundir com o Instagram Login (OAuth proprio do IG),
       * que depende de permissao em analise na Meta.
       */
      const SCOPES = {
        whatsapp: "whatsapp_business_management,whatsapp_business_messaging",
        facebook: "pages_show_list,pages_messaging,pages_manage_metadata,pages_read_engagement",
        instagram:
          "instagram_basic,instagram_manage_messages,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management",
      };
      const scope = SCOPES[cfg.channel] || SCOPES.facebook;
      // Facebook/Instagram (login por PAGINA, sem config_id): forca o SELETOR DE PAGINAS
      // a reaparecer toda vez. Sem isto, quem ja autorizou o app uma vez (ainda que sem
      // marcar a pagina, ou tendo-a desmarcado) recebe o grant antigo em SILENCIO: o
      // `/me/accounts` volta vazio e o HUB conclui "Nenhuma pagina foi liberada" — sem o
      // cliente conseguir reescolher. `enable_profile_selector` reabre a lista de paginas
      // e `auth_type: rerequest` reexibe o dialogo mesmo com permissoes ja concedidas.
      const extras = (cfg.channel === "whatsapp" && cfg.configId)
        ? { config_id: cfg.configId, override_default_response_type: true, response_type: "code" }
        : { auth_type: "rerequest", enable_profile_selector: true };
      // Coexistencia (WhatsApp Business app onboarding): featureType oficial que
      // dispara o fluxo de coexistencia (QR no celular). SEM isso a Meta cai no
      // fluxo WABA padrao (criar conta / selecionar ativos), mesmo com o config_id.
      if (cfg.coexist) {
        extras.extras = {
          setup: {},
          featureType: "whatsapp_business_app_onboarding",
        };
      }
      window.FB.login((response) => {
        const code = response.authResponse?.code;
        const accessToken = response.authResponse?.accessToken;
        if (code) complete({ code });
        else if (accessToken) complete({ accessToken });
        else setError("Autorizacao cancelada.");
      }, { scope, ...extras });
    } catch (e) {
      setError(e.message || "Erro ao iniciar o SDK do Facebook.");
    }
  }, [cfg, loadFacebookSDK, complete]);

  // Auto-abre o Embedded Signup assim que a config carrega — o clique no card do
  // canal (que abriu este popup) ja foi o gesto do usuario, entao o card "sabe"
  // abrir o Meta embed pelo Central sem um segundo clique. O botao "Autorizar pela
  // Meta" fica como fallback caso o navegador bloqueie o popup automatico.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (phase === "ready" && !autoStarted.current) {
      autoStarted.current = true;
      start();
    }
  }, [phase, start]);

  const wrap = (children) => (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#0B0F19", p: 3 }}>
      <Box sx={{ maxWidth: 420, width: "100%", textAlign: "center", bgcolor: "#111726", borderRadius: 3, p: 4, border: "1px solid rgba(255,255,255,0.06)" }}>
        {children}
      </Box>
    </Box>
  );

  if (phase === "working" && escolha) return wrap(<><CircularProgress sx={{ color: "#6B48FF" }} /><Typography sx={{ mt: 2, color: "#9CA3AF" }}>Conectando as contas marcadas...</Typography></>);

  if (phase === "loading") return wrap(<><CircularProgress sx={{ color: "#6B48FF" }} /><Typography sx={{ mt: 2, color: "#9CA3AF" }}>Preparando conexao segura...</Typography></>);

  if (phase === "error") return wrap(
    <>
      <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>{error}</Alert>
      <Typography variant="body2" sx={{ color: "#6B7280" }}>Feche esta janela e tente novamente pelo painel.</Typography>
    </>
  );

  if (phase === "escolher" && escolha) {
    const novasMarcadas = escolha.contas.filter((c) => marcadas.includes(c.id) && !c.jaConectada).length;
    const semVaga = (conta) => escolha.vagas != null && !conta.jaConectada && !marcadas.includes(conta.id) && novasMarcadas >= escolha.vagas;
    const alternar = (conta) => {
      if (semVaga(conta)) return;
      setMarcadas((m) => (m.includes(conta.id) ? m.filter((x) => x !== conta.id) : [...m, conta.id]));
    };
    return wrap(
      <>
        <Typography variant="h6" sx={{ color: "#fff", mb: 0.5 }}>
          {escolha.canal === "instagram" ? "Quais contas do Instagram conectar?" : "Quais paginas do Facebook conectar?"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#9CA3AF", mb: 2 }}>
          {escolha.vagas == null
            ? "Cada conta marcada vira uma conexao."
            : `Seu plano permite mais ${escolha.vagas} ${escolha.vagas === 1 ? "conexao" : "conexoes"}. Contas ja conectadas nao ocupam vaga.`}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2, textAlign: "left" }} onClose={() => setError("")}>{error}</Alert>}
        <Box sx={{ textAlign: "left", maxHeight: 340, overflowY: "auto", mb: 2 }}>
          {escolha.contas.map((conta) => {
            const bloqueada = semVaga(conta);
            return (
              <Box
                key={conta.id}
                onClick={() => alternar(conta)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1, p: 1, mb: 0.5, borderRadius: 2,
                  cursor: bloqueada ? "not-allowed" : "pointer", opacity: bloqueada ? 0.45 : 1,
                  border: "1px solid", borderColor: marcadas.includes(conta.id) ? "#6B48FF" : "rgba(255,255,255,0.08)",
                }}
              >
                <Checkbox checked={marcadas.includes(conta.id)} disabled={bloqueada} size="small" sx={{ color: "#9CA3AF", p: 0.5 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ color: "#fff", fontSize: 14 }} noWrap>
                    {conta.instagram ? `@${conta.instagram}` : conta.nome}
                  </Typography>
                  {conta.instagram && <Typography sx={{ color: "#6B7280", fontSize: 12 }} noWrap>Pagina: {conta.nome}</Typography>}
                </Box>
                {conta.jaConectada && <Chip label="ja conectada" size="small" sx={{ bgcolor: "rgba(34,197,94,0.15)", color: "#22C55E" }} />}
              </Box>
            );
          })}
        </Box>
        <Button
          variant="contained" size="large" fullWidth disabled={marcadas.length === 0}
          onClick={() => { setError(""); complete({ contas: marcadas }); }}
          sx={{ py: 1.3, background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)", "&:hover": { opacity: 0.9 } }}
        >
          {marcadas.length === 1 ? "Conectar 1 conta" : `Conectar ${marcadas.length} contas`}
        </Button>
      </>
    );
  }

  if (phase === "done") return wrap(
    <>
      <CheckCircleIcon sx={{ fontSize: 64, color: "#22C55E", mb: 1 }} />
      <Typography variant="h6" sx={{ color: "#fff" }}>
        {result?.conexoes?.length > 1 ? `${result.conexoes.length} contas conectadas!` : "Conectado com sucesso!"}
      </Typography>
      {result?.conexoes?.length > 1 && (
        <Typography variant="body2" sx={{ color: "#9CA3AF", mt: 1 }}>{result.conexoes.map((c) => c.pageName).filter(Boolean).join(", ")}</Typography>
      )}
      {result?.falhas?.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2, textAlign: "left" }}>
          Nao conectadas: {result.falhas.map((x) => `${x.nome} (${x.erro})`).join("; ")}
        </Alert>
      )}
      {result?.phoneNumber && <Typography variant="body2" sx={{ color: "#9CA3AF", mt: 1 }}>Numero: {result.phoneNumber}</Typography>}
      <Typography variant="body2" sx={{ color: "#6B7280", mt: 2 }}>Pode fechar esta janela.</Typography>
    </>
  );

  // ready | working
  return wrap(
    <>
      <Typography variant="h6" sx={{ color: "#fff", mb: 1 }}>Conectar canal</Typography>
      <Typography variant="body2" sx={{ color: "#9CA3AF", mb: 3 }}>
        Autorize sua conta pela Meta. Este passo roda com seguranca no HUB Central.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2, textAlign: "left" }} onClose={() => setError("")}>{error}</Alert>}
      <Button
        variant="contained" size="large" onClick={start} disabled={phase === "working"}
        sx={{ py: 1.5, px: 5, background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)", "&:hover": { opacity: 0.9 } }}
      >
        {phase === "working" ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Autorizar pela Meta"}
      </Button>
    </>
  );
}
